// Seat a booking or a waiting party (F-RSV-N1). "Seat & open tab" opens the
// table's tab through the same endpoint the floor uses
// (POST /counter/table-tabs — which stamps the business day and refuses a
// table that already has an open order), links that order to the booking,
// then hands over to the counter with the tab loaded. "Seat only" records
// the arrival without opening anything (e.g. the floor already opened it).
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { useReservationHolds } from '@/hooks/useReservations'
import { invalidateTables } from '@/lib/tablesCache'
import { cn } from '@/lib/utils'
import { holdLabel } from '@/lib/reservations'
import type { DiningTable } from '@/types'

export interface SeatTarget {
  kind: 'reservation' | 'waitlist'
  id: string
  guestName: string
  guestPhone?: string
  partySize: number
  notes?: string
  /** Tables the booking holds; the first is preselected. */
  tableIds: string[]
}

export function SeatDialog({
  target,
  tables,
  onOpenChange,
}: {
  target: SeatTarget | null
  tables: DiningTable[]
  onOpenChange: (o: boolean) => void
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()
  const holds = useReservationHolds()
  const [tableId, setTableId] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!target) return
    setError(null)
    setBusy(false)
    setTableId(target.tableIds[0] ?? '')
  }, [target])

  const sorted = useMemo(
    () => [...tables].sort((a, b) => String(a.table_number).localeCompare(String(b.table_number), undefined, { numeric: true })),
    [tables],
  )
  const chosen = sorted.find((t) => t.id === tableId)
  const chosenBusy = Boolean(chosen && (chosen.has_active_order ?? chosen.is_occupied))
  const otherHold = chosen ? holds.get(chosen.id) : undefined
  const holdForSomeoneElse = otherHold && !(target?.kind === 'reservation' && otherHold.reservation_id === target.id)

  const link = async (orderId?: string, tid?: string) => {
    if (!target) return
    if (target.kind === 'reservation') {
      await apiClient.reservationAction(target.id, 'seat', orderId ? { order_id: orderId } : {})
    } else {
      await apiClient.waitlistAction(target.id, 'seat', { ...(tid ? { table_id: tid } : {}), ...(orderId ? { order_id: orderId } : {}) })
    }
  }

  const seatAndOpen = async () => {
    if (!target || !chosen) return
    setBusy(true)
    setError(null)
    try {
      const res = await apiClient.openCounterTableTab({
        table_id: chosen.id,
        guest_count: target.partySize,
        customer_name: target.guestName,
        customer_phone: target.guestPhone || undefined,
        notes: target.notes || undefined,
      })
      if (!res.success || !res.data) throw new Error(res.message || 'Could not open the table')
      await link(res.data.id, chosen.id)
      invalidateTables(qc)
      qc.invalidateQueries({ queryKey: ['reservations'] })
      toast({ title: `${target.guestName} seated`, description: `Table ${chosen.table_number} is open on the counter.` })
      onOpenChange(false)
      navigate({ to: '/admin/counter', search: { openTableId: chosen.id } })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not seat the guest')
      setBusy(false)
    }
  }

  const seatOnly = async () => {
    if (!target) return
    setBusy(true)
    setError(null)
    try {
      await link(undefined, tableId || undefined)
      qc.invalidateQueries({ queryKey: ['reservations'] })
      toast({ title: `${target.guestName} seated` })
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not seat the guest')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={Boolean(target)} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Seat {target?.guestName}</DialogTitle>
          <DialogDescription>
            {target?.partySize} {target?.partySize === 1 ? 'guest' : 'guests'}. Pick the table to open their tab on.
          </DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[50vh] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
          {sorted.map((t) => {
            const open = Boolean(t.has_active_order ?? t.is_occupied)
            const held = holds.get(t.id)
            const mine = held && target?.kind === 'reservation' && held.reservation_id === target.id
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTableId(t.id)}
                aria-pressed={tableId === t.id}
                className={cn(
                  'flex flex-col items-start rounded-lg border bg-card px-3 py-2 text-left text-sm transition-colors hover:border-primary/50',
                  tableId === t.id && 'border-primary ring-2 ring-primary/40',
                  open && 'border-emerald-300 bg-emerald-50/70 dark:border-emerald-800 dark:bg-emerald-950/30',
                )}
              >
                <span className="font-semibold">{t.table_number}</span>
                <span className="text-xs text-muted-foreground">
                  {t.seating_capacity} seats{open ? ' · open tab' : ''}
                </span>
                {held && !mine ? <span className="text-xs font-medium text-amber-700 dark:text-amber-300">{holdLabel(held)}</span> : null}
              </button>
            )
          })}
        </div>
        {chosenBusy ? (
          <p className="text-sm text-muted-foreground">
            Table {chosen?.table_number} already has an open tab. Pick a free table, or use &ldquo;Seat only&rdquo; if
            that tab is theirs.
          </p>
        ) : null}
        {holdForSomeoneElse && otherHold ? (
          <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
            Table {chosen?.table_number} is held for {otherHold.guest_name} ({otherHold.party_size}) — {holdLabel(otherHold)}.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Close
          </Button>
          <Button variant="secondary" onClick={seatOnly} disabled={busy}>
            Seat only
          </Button>
          <Button onClick={seatAndOpen} disabled={busy || !chosen || chosenBusy}>
            {busy ? 'Seating…' : 'Seat & open tab'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
